import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Trash2, Printer, RotateCcw, ShoppingCart } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api from '../../lib/api';

const fmtRs = (v) => `Rs. ${Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const decimalPayloadValue = (value) => String(value).trim();

const incrementDocumentQuantityString = (value) => {
  const text = String(value ?? '').trim();
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(text);
  if (!match) return '1';

  const fractionalText = (match[2] || '').padEnd(2, '0');
  const hundredths = (BigInt(match[1]) * 100n) + BigInt(fractionalText) + 100n;
  const whole = hundredths / 100n;
  const fraction = hundredths % 100n;
  if (fraction === 0n) return String(whole);
  if (fraction % 10n === 0n) return `${whole}.${fraction / 10n}`;
  return `${whole}.${String(fraction).padStart(2, '0')}`;
};

const QuickSale = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [walkInName] = useState('Walk-in Customer');
  const [items, setItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [gstRate, setGstRate] = useState('0');

  // Product search
  const [allProducts, setAllProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const qtyRefs = useRef([]);
  const [focusQtyIdx, setFocusQtyIdx] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem('token');
    const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    api.get('/customers').then(r => setCustomers(r.data.items ?? r.data ?? [])).catch(() => {});
    api.get('/products?limit=1000').then(r => {
      setAllProducts((r.data.items ?? r.data.products ?? []).filter(p => p.is_active));
    }).catch(() => {});
    api.get('/tenant/profile').then(r => {
      const rate = r.data.gst_registered ? (r.data.gst_rate ?? 0) : 0;
      setGstRate(String(rate));
    }).catch(() => {});
  }, [navigate]);

  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Auto-focus quantity field when a new product row is added
  useEffect(() => {
    if (focusQtyIdx !== null && qtyRefs.current[focusQtyIdx]) {
      qtyRefs.current[focusQtyIdx].focus();
      setFocusQtyIdx(null);
    }
  }, [focusQtyIdx, items]);

  const searchProducts = (q) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults([]); setShowDropdown(false); return; }
    const lower = q.toLowerCase();
    const results = allProducts.filter(p =>
      p.name.toLowerCase().includes(lower) || (p.sku && p.sku.toLowerCase().includes(lower))
    ).slice(0, 20);
    setSearchResults(results);
    setShowDropdown(true);
  };

  const addProduct = (product) => {
    const existingIdx = items.findIndex(i => i.product_id === product.id);
    if (existingIdx !== -1) {
      setItems(items.map((i, idx) => idx === existingIdx
        ? { ...i, quantity: incrementDocumentQuantityString(i.quantity) }
        : i));
      setFocusQtyIdx(existingIdx);
    } else {
      const newIdx = items.length;
      const units = product.units || [];
      const defaultUnit = units.find(u => u.is_default);
      const unitId = defaultUnit?.id || '';
      const unitPrice = defaultUnit?.selling_price || product.selling_price;
      setItems([...items, {
        product_id: product.id,
        name: product.name,
        sku: product.sku,
        unit_price: unitPrice,
        quantity: '1',
        discount_percent: 0,
        max_stock: product.current_stock,
        unit: product.unit,
        base_unit: product.base_unit || product.unit,
        unit_id: unitId,
        units: units,
      }]);
      setFocusQtyIdx(newIdx);
    }
    setSearchQuery('');
    setShowDropdown(false);
  };

  const updateItemUnit = (idx, unitId) => {
    setItems(items.map((item, i) => {
      if (i !== idx) return item;
      const newItem = { ...item, unit_id: unitId };
      if (unitId) {
        const u = (item.units || []).find(u => u.id === unitId);
        if (u && u.selling_price) newItem.unit_price = u.selling_price;
      }
      return newItem;
    }));
  };

  const updateItem = (idx, field, val) => {
    setItems(items.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const subtotal = items.reduce(
    (s, i) => s + (Number(i.quantity || 0) * i.unit_price * (1 - i.discount_percent / 100)),
    0
  );
  const gstRateNum = parseFloat(gstRate) || 0;
  const gstAmount = subtotal * (gstRateNum / 100);
  const grandTotal = subtotal + gstAmount;

  const completeSale = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        customer_id: customerId || null,
        walk_in_name: walkInName,
        items: items.map(i => ({
          product_id: i.product_id,
          unit_id: i.unit_id || null,
          quantity: decimalPayloadValue(i.quantity),
          unit_price: decimalPayloadValue(i.unit_price),
          discount_percent: decimalPayloadValue(i.discount_percent),
        })),
        payment_method: paymentMethod,
        notes: notes || null,
        gst_rate: gstRate !== '' ? decimalPayloadValue(gstRate) : null,
      };
      const r = await api.post('/quick-sale', payload);
      if (r.data.success) {
        setReceipt(r.data.receipt);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to complete sale';
      alert(msg);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setReceipt(null);
    setItems([]);
    setCustomerId('');
    setPaymentMethod('cash');
    setNotes('');
    setSearchQuery('');
  };

  const printReceipt = () => {
    window.print();
  };

  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
  };

  if (receipt) {
    return (
      <div className="flex h-screen bg-[#f0f2f5]" data-testid="quick-sale-receipt">
        <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
          <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
          <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
            <div className="max-w-lg mx-auto">
              <div className="bg-white border border-[#e5e7eb] rounded-2xl p-4 sm:p-6 print:border-0 print:shadow-none" data-testid="receipt-card">
                <div className="text-center border-b border-dashed border-[#e5e7eb] pb-4 mb-4">
                  <h2 className="text-lg font-bold text-[#4A7AE0]">SAMS ERP</h2>
                  <p className="text-xs text-[#6b7280]">Sales Receipt</p>
                </div>
                <div className="text-sm text-[#6b7280] mb-4 space-y-1">
                  <p><span className="font-medium text-[#1a1a2e]">Invoice:</span> {receipt.invoice_number}</p>
                  <p><span className="font-medium text-[#1a1a2e]">Date:</span> {fmtDate(receipt.date)}</p>
                  <p><span className="font-medium text-[#1a1a2e]">Customer:</span> {receipt.customer_name}</p>
                  <p><span className="font-medium text-[#1a1a2e]">Payment:</span> {receipt.payment_method}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm mb-4 min-w-[400px]">
                  <thead className="border-b border-[#e5e7eb]">
                    <tr className="text-[#6b7280]">
                      <th className="py-2 text-left">Item</th>
                      <th className="py-2 text-right">Qty</th>
                      <th className="py-2 text-right">Price</th>
                      <th className="py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receipt.items.map((item, i) => (
                      <tr key={i} className="border-b border-[#e5e7eb]">
                        <td className="py-2 text-[#1a1a2e]">{item.product_name}</td>
                        <td className="py-2 text-right text-[#6b7280]">{item.quantity}</td>
                        <td className="py-2 text-right text-[#6b7280]">{fmtRs(item.unit_price)}</td>
                        <td className="py-2 text-right text-[#1a1a2e]">{fmtRs(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <div className="space-y-1 text-sm border-t border-dashed border-[#e5e7eb] pt-3">
                  <div className="flex justify-between text-[#6b7280]"><span>Subtotal</span><span>{fmtRs(receipt.subtotal)}</span></div>
                  {receipt.discount_total > 0 && <div className="flex justify-between text-[#6b7280]"><span>Discount</span><span>-{fmtRs(receipt.discount_total)}</span></div>}
                  {receipt.gst_amount > 0 && <div className="flex justify-between text-[#6b7280]"><span>GST ({receipt.gst_rate}%)</span><span>{fmtRs(receipt.gst_amount)}</span></div>}
                  <div className="flex justify-between font-bold text-lg text-[#1a1a2e] border-t border-[#e5e7eb] pt-2 mt-2" data-testid="receipt-total">
                    <span>TOTAL</span><span>{fmtRs(receipt.total_amount)}</span>
                  </div>
                </div>
                <div className="text-center mt-4 text-xs text-[#9ca3af]">
                  <p>Status: PAID</p>
                  <p className="mt-1">Thank you for your purchase!</p>
                </div>
              </div>
              <div className="flex gap-3 mt-4 print:hidden">
                <button onClick={printReceipt} className="flex-1 flex items-center justify-center gap-2 bg-[#1a1a2e] text-white px-4 py-3 rounded-sm text-sm hover:bg-[#27272A] transition-colors" data-testid="print-receipt-btn">
                  <Printer className="w-4 h-4" />Print Receipt
                </button>
                <button onClick={resetForm} className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white px-4 py-3 rounded-lg text-sm transition-colors" data-testid="new-sale-btn">
                  <RotateCcw className="w-4 h-4" />New Sale
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="quick-sale-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1a1a2e] mb-4 sm:mb-6" data-testid="quick-sale-title">Quick Sale</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: items entry */}
            <div className="lg:col-span-2 space-y-4">
              {/* Customer */}
              <div className="bg-white border border-[#e5e7eb] rounded-2xl p-4">
                <label className="block text-xs font-medium text-[#6b7280] mb-1">Customer</label>
                <select value={customerId} onChange={e => setCustomerId(e.target.value)} className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm" data-testid="customer-select">
                  <option value="">Walk-in Customer (Default)</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {/* Product Search */}
              <div className="bg-white border border-[#e5e7eb] rounded-2xl p-4">
                <label className="block text-xs font-medium text-[#6b7280] mb-1">Add Product</label>
                <div className="relative" ref={searchRef}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => searchProducts(e.target.value)}
                      onFocus={() => { setSearchResults(allProducts.slice(0, 100)); setShowDropdown(true); }}
                      placeholder="Click to browse or type to search..."
                      className="w-full border border-[#e5e7eb] rounded-lg pl-10 pr-3 py-2 text-sm"
                      data-testid="product-search-input"
                    />
                  </div>
                  {showDropdown && searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-[#e5e7eb] rounded-xl shadow-lg max-h-60 overflow-y-auto" data-testid="product-dropdown">
                      {searchResults.map(p => {
                        const outOfStock = p.current_stock <= 0;
                        return (
                          <button
                            key={p.id}
                            onClick={() => !outOfStock && addProduct(p)}
                            disabled={outOfStock}
                            className={`w-full flex items-center justify-between px-4 py-2 text-sm border-b border-[#e5e7eb] last:border-0 ${outOfStock ? 'opacity-50 cursor-not-allowed bg-[#f9fafb]' : 'hover:bg-[#f9fafb]'}`}
                            data-testid={`product-option-${p.sku}`}
                          >
                            <div className="text-left">
                              <p className={`font-medium ${outOfStock ? 'text-[#EF4444]' : 'text-[#1a1a2e]'}`}>
                                {p.name} ({p.sku})
                              </p>
                              <p className={`text-xs ${outOfStock ? 'text-[#EF4444]' : 'text-[#9ca3af]'}`}>
                                Stock: {p.current_stock} {p.unit}{outOfStock ? ' — Out of stock' : ''}
                              </p>
                            </div>
                            <span className="text-[#4A7AE0] font-medium ml-4 shrink-0">{fmtRs(p.selling_price)}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Items Table */}
              {items.length > 0 && (
                <div className="bg-white border border-[#e5e7eb] rounded-2xl overflow-hidden overflow-x-auto">
                  <table className="w-full text-sm min-w-[600px]" data-testid="items-table">
                    <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                      <tr>
                        <th className="py-3 px-4 text-left">Product</th>
                        <th className="py-3 px-3 text-center w-24">Qty</th>
                        <th className="py-3 px-3 text-right w-32">Price</th>
                        <th className="py-3 px-3 text-center w-24">Disc %</th>
                        <th className="py-3 px-3 text-right w-32">Total</th>
                        <th className="py-3 px-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, i) => {
                        const lineTotal = Number(item.quantity || 0) * item.unit_price * (1 - item.discount_percent / 100);
                        return (
                          <tr key={i} className="border-b border-[#e5e7eb]">
                            <td className="py-3 px-4">
                              <p className="font-normal text-[#1a1a2e]">{item.name}</p>
                              <p className="text-xs text-[#9ca3af]">{item.sku}</p>
                            </td>
                            <td className="py-3 px-3">
                              <input type="number" min="0.01" step="0.01" max={item.max_stock} value={item.quantity}
                                placeholder="0"
                                onChange={e => updateItem(i, 'quantity', e.target.value)}
                                className="w-full border border-[#e5e7eb] rounded-lg px-2 py-1 text-sm text-center"
                                data-testid={`qty-input-${i}`}
                                ref={el => qtyRefs.current[i] = el}
                              />
                            </td>
                            <td className="py-3 px-3">
                              <input type="number" min="0" step="0.01" value={item.unit_price === 0 ? '' : item.unit_price}
                                placeholder="0"
                                onChange={e => updateItem(i, 'unit_price', parseFloat(e.target.value) || 0)}
                                className="w-full border border-[#e5e7eb] rounded-lg px-2 py-1 text-sm text-right"
                                data-testid={`price-input-${i}`}
                              />
                            </td>
                            <td className="py-3 px-3">
                              <input type="number" min="0" max="100" value={item.discount_percent === 0 ? '' : item.discount_percent}
                                placeholder="0"
                                onChange={e => updateItem(i, 'discount_percent', Math.min(100, parseFloat(e.target.value) || 0))}
                                className="w-full border border-[#e5e7eb] rounded-lg px-2 py-1 text-sm text-center"
                                data-testid={`disc-input-${i}`}
                              />
                            </td>
                            <td className="py-3 px-3 text-right font-normal text-[#1a1a2e]">{fmtRs(lineTotal)}</td>
                            <td className="py-3 px-2">
                              <button onClick={() => removeItem(i)} className="text-[#EF4444] hover:text-[#B91C1C]" data-testid={`remove-item-${i}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {items.length === 0 && (
                <div className="bg-white border border-[#e5e7eb] border-dashed rounded-2xl p-12 text-center">
                  <ShoppingCart className="w-10 h-10 mx-auto text-[#D4D4D4] mb-3" />
                  <p className="text-[#9ca3af] text-sm">Search and add products to start a sale</p>
                </div>
              )}
            </div>

            {/* Right: totals & payment */}
            <div className="space-y-4">
              <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5 sticky top-6">
                <h3 className="text-sm font-semibold text-[#6b7280] uppercase tracking-wider mb-4">Order Summary</h3>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-[#6b7280] mb-1">GST %</label>
                  <input
                    type="number" min="0" max="100" step="0.01"
                    value={gstRate}
                    onChange={e => setGstRate(e.target.value)}
                    className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                    data-testid="gst-rate-input"
                  />
                </div>
                <div className="space-y-2 text-sm border-b border-[#e5e7eb] pb-4 mb-4">
                  <div className="flex justify-between text-[#6b7280]"><span>Subtotal</span><span data-testid="subtotal">{fmtRs(subtotal)}</span></div>
                  {gstRateNum > 0 && (
                    <div className="flex justify-between text-[#6b7280]">
                      <span>GST ({gstRateNum}%)</span>
                      <span data-testid="gst-amount">{fmtRs(gstAmount)}</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-between text-xl font-bold text-[#1a1a2e] mb-6" data-testid="grand-total">
                  <span>Total</span><span>{fmtRs(grandTotal)}</span>
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-medium text-[#6b7280] mb-1">Payment Method</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['cash', 'bank_transfer', 'cheque'].map(m => (
                      <button key={m} onClick={() => setPaymentMethod(m)}
                        className={`px-3 py-2.5 text-sm rounded-lg border transition-colors ${paymentMethod === m
                          ? 'bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white border-transparent'
                          : 'bg-white text-[#6b7280] border-[#e5e7eb] hover:border-[#4A7AE0]'}`}
                        data-testid={`payment-${m}`}
                      >
                        {m === 'cash' ? 'Cash' : m === 'bank_transfer' ? 'Bank' : 'Cheque'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-medium text-[#6b7280] mb-1">Notes (optional)</label>
                  <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Regular customer"
                    className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm" data-testid="notes-input" />
                </div>

                <button
                  onClick={completeSale}
                  disabled={items.length === 0 || loading}
                  className="w-full bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white py-4 rounded-lg text-base font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="complete-sale-btn"
                >
                  {loading ? 'Processing...' : 'Complete Sale'}
                </button>

                <p className="text-xs text-[#9ca3af] text-center mt-3">
                  Creates invoice, records payment, deducts stock
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default QuickSale;
